// App.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router } from "react-router-dom";
import { Snackbar, Alert, Button } from "@mui/material";
import AppRoutes from "./routes";
import { PageProvider } from "./context/PageContext";
import { AuthContext, isWebAuthnGestureDenied } from "./context/AuthContext";
import { readStoredLocale } from "./i18n/config";
import { getLoginUiStrings } from "./i18n/loginUiStrings";
import { SocketProvider } from "./context/SocketContext";
import "reactflow/dist/style.css";
import { CallProvider } from "./context/CallContext";
import { NotificationProvider } from "./context/NotificationContext";
import { TeamPresenceProvider } from "./context/TeamPresenceContext";
import { WorkspaceConfigProvider } from "./context/WorkspaceConfigContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { useBrandAssets } from "./hooks/useBrandAssets";
import { useRemoteI18nBundle } from "./hooks/useRemoteI18nBundle";
import UrlLocaleSync from "./i18n/UrlLocaleSync";
import TeamPresenceRouteSync from "./components/TeamPresenceRouteSync";
import TeamPresenceSocketEmitter from "./components/TeamPresenceSocketEmitter";
import ProductUsageTracker from "./components/ProductUsageTracker";
import { AdvancedSearchProvider } from "./context/AdvancedSearchContext";
import { PlatformAiProvider } from "./context/PlatformAiContext";
import AppNavigateBridge from "./components/AppNavigateBridge";

function I18nRemoteSync() {
  useRemoteI18nBundle();
  return null;
}

const BIOMETRIC_REGISTER_NOTICE_KEY = "xproBiometricRegisterNotice";
const BIOMETRIC_ENROLL_OFFER_KEY = "xproOfferBiometricEnroll";

function resolveEnrollEmail(userData) {
  return String(
    userData?.email ||
      (typeof localStorage !== "undefined" ? localStorage.getItem("savedEmail") : "") ||
      ""
  ).trim();
}

function BiometricRegisterNotice() {
  const { registerBiometric, prepareBiometricChallenge, userData } = useContext(AuthContext);
  const L = getLoginUiStrings(readStoredLocale() || "pt-BR");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("error");
  const [message, setMessage] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const prefetchedChallengeRef = useRef("");

  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(BIOMETRIC_REGISTER_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(BIOMETRIC_REGISTER_NOTICE_KEY);
        sessionStorage.removeItem(BIOMETRIC_ENROLL_OFFER_KEY);
        setMode("error");
        setMessage(notice);
        setOpen(true);
        return;
      }
      const offer = sessionStorage.getItem(BIOMETRIC_ENROLL_OFFER_KEY);
      const alreadyEnrolled =
        typeof localStorage !== "undefined" && localStorage.getItem("biometricId");
      if (offer === "1" && !alreadyEnrolled) {
        sessionStorage.removeItem(BIOMETRIC_ENROLL_OFFER_KEY);
        setMode("offer");
        setMessage(L.biometricEnrollOffer);
        setOpen(true);
        const email = resolveEnrollEmail(userData);
        if (email && typeof prepareBiometricChallenge === "function") {
          prepareBiometricChallenge(email)
            .then((challenge) => {
              prefetchedChallengeRef.current = challenge || "";
            })
            .catch(() => {
              prefetchedChallengeRef.current = "";
            });
        }
      }
    } catch {
      /* ignore */
    }
  }, [L.biometricEnrollOffer, prepareBiometricChallenge, userData]);

  const handleEnroll = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const email = resolveEnrollEmail(userData);
    if (enrolling) return;
    if (!email) {
      setMode("error");
      setMessage(L.biometricRegisterFailed);
      return;
    }
    setEnrolling(true);
    try {
      const cached = prefetchedChallengeRef.current;
      await registerBiometric(email, cached ? { challenge: cached } : {});
      setOpen(false);
    } catch (err) {
      if (isWebAuthnGestureDenied(err)) {
        setOpen(false);
        return;
      }
      setMode("error");
      setMessage(err?.message || L.biometricRegisterFailed);
    } finally {
      setEnrolling(false);
    }
  };

  if (!message) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={mode === "offer" ? 16000 : 8000}
      onClose={(event, reason) => {
        if (mode === "offer" && reason === "clickaway") return;
        setOpen(false);
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity={mode === "offer" ? "info" : "warning"}
        onClose={() => setOpen(false)}
        sx={{ width: "100%" }}
        action={
          mode === "offer" ? (
            <Button color="inherit" size="small" disabled={enrolling} onClick={handleEnroll}>
              {enrolling ? "..." : L.biometricEnrollAction}
            </Button>
          ) : null
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
}

const AuthenticatedApp = () => (
  <SocketProvider>
    <I18nRemoteSync />
    <PageProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <UrlLocaleSync />
        <AppNavigateBridge />
        <TeamPresenceRouteSync />
        <ProductUsageTracker />
        <BiometricRegisterNotice />
        <TeamPresenceSocketEmitter />
        <TeamPresenceProvider>
        <NotificationProvider>
          <CallProvider>
            {/* <== Provider de Configurações do Workspace */}
            <WorkspaceConfigProvider>
              <OnboardingProvider>
                <AdvancedSearchProvider>
                  <PlatformAiProvider>
                    <AppRoutes />
                  </PlatformAiProvider>
                </AdvancedSearchProvider>
              </OnboardingProvider>
            </WorkspaceConfigProvider>
          </CallProvider>
        </NotificationProvider>
        </TeamPresenceProvider>
      </Router>
    </PageProvider>
  </SocketProvider>
);

const App = () => {
  useBrandAssets();
  const { t } = useTranslation("app");
  const { bearerToken, isAuthenticating } = useContext(AuthContext);

  if (isAuthenticating) {
    return <div>{t("loading")}</div>;
  }

  return bearerToken ? (
    <AuthenticatedApp />
  ) : (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <UrlLocaleSync />
      <AppRoutes />
    </Router>
  );
};

export default App;
